import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import moment from 'moment'
import _ from 'lodash'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextSearch from 'component/Input/InputTextSearch'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import DoctorFeeApi from 'api/setting/doctor-fee-managements/doctor-fee.api'
import { routeName } from 'routes/routes-name'
import { useHistory } from 'react-router-dom'
import { numberFormat, toBuddhistYear } from 'utils/app.utils'
import CreditFeeApi from 'api/setting/credit-fee-managements/credit-fee.api'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
export default function CreditFee() {
  const { t } = useTranslation()
  const history = useHistory()

  const [creditFees, setCreditFee] = useState([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const status = ''

  const loadData = useCallback(async () => {
    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status }

    const res = await CreditFeeApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setCreditFee(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const onCreate = () => {
    history.push(routeName.creditFee + '/create')
  }

  const onActive = (id: number, valueStatus: string) => {
    swalActive(`<p class="mb-0 mx-auto" style="max-width: 325px">${t('CREDIT_FEE.ALERT.CONFIRM_STATUS')}</p>`, null, (res: any) => {
      if (res) {
        CreditFeeApi.updateStatus(id, valueStatus)
          .then((resp) => {
            notiSuccess(t('CREDIT_FEE.MESSAGE.SUCCESS.UPDATE_STATUS'))
            loadData()
          })
          .catch((e) => {
            notiError(t('CREDIT_FEE.MESSAGE.ERROR'))
          })
      }
    }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL'))
  }
  const onDelete = (data: any) => {
    const { CreditFeeId, CreditFeeName } = data

    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('CREDIT_FEE.ALERT.CONFIRM_DELETE')}</p>`,
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('CREDIT_FEE.ALERT.CONFIRM_DELETE_', {
        creditFeeName: CreditFeeName
      })}</p>`,
      (res: any) => {
        if (res) {
          CreditFeeApi.remove(CreditFeeId)
            .then((resp) => {
              notiSuccess(t('CREDIT_FEE.MESSAGE.SUCCESS.DELETE'))
              loadData()
            })
            .catch((e) => {
              notiError(t('CREDIT_FEE.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
    )
  }

  const onEdit = (data: any) => {
    const { CreditFeeId } = data
    history.push(`${routeName.creditFee}/${CreditFeeId}`)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#', width: '60px' },
    { id: 'creditFeeName', disablePadding: false, label: t(`CREDIT_FEE.FORM.CF_NAME`) },
    // { id: 'amount', disablePadding: false, label: t(`CREDIT_FEE.FORM.CF_B`), width: '185px' },
    { id: 'dateStart', disablePadding: false, label: t('CREDIT_FEE.FORM.USE_DATE'), width: '130px' },
    { id: 'templateDoctorFee', disablePadding: false, label: t('CREDIT_FEE.FORM.TEMPLATE_DF') },
    { id: 'doctors', disablePadding: false, label: t('USER_DOCTOR'), width: '250px' },
    { id: 'status', disablePadding: false, label: t('TABLE.CELL.STATUS'), width: '200px' },
    { id: 'updatedBy', disablePadding: false, label: t('TABLE.CELL.UPDATED_BY'), width: '200px' },
    { id: 'action', disablePadding: false, label: 'Action', width: '80px' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { CreditFeeId, CreditFeeName, amount, dateStart, doctors, status: dfStatus, updatedAt, updatedBy } = objData

    let doctor = doctors ? doctors.split(',') : []
    if (!_.isEmpty(doctor) && _.size(doctor) > 1) doctor = `${doctor[0]} และอีก ${_.size(doctor) - 1} คน`
    else if (!_.isEmpty(doctor)) doctor = doctor[0]
    else doctor = '-'

    const statusBtnActive = dfStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = dfStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const templateCreditFeeName = objData.templateCreditFee ? objData.templateCreditFee.templateCreditFeeName : '-'
    const objRenderData = {
      key: CreditFeeId,
      id: CreditFeeId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: CreditFeeName, class: 'text-ellipsis' },
        // { option: 'TEXT', align: 'left', label: `${amount}:${3 - amount > 0 ? numberFormat(3 - amount, 0, 3) : 0}` },
        { option: 'TEXT', align: 'left', label: toBuddhistYear(moment(dateStart), 'DD/MM/YYYY') },
        { option: 'TEXT', align: 'left', label: templateCreditFeeName, class: 'text-ellipsis' },
        { option: 'TEXT', align: 'left', label: doctor },
        { option: 'STATUS', align: 'center', label: dfStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => onActive(Number(objRenderData.id), 'ACTIVE')}
        oninactive={() => onActive(Number(objRenderData.id), 'INACTIVE')}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => onDelete(objRenderData.obj)}
      />
    )
  }

  return (
    <div>
      <Card className="border-0 h-100">
        <HeaderCard text={t('MENU.CREDIT_FEE')} />
        <Card.Body>
          <Card.Title>
            <Row className="align-items-center">
              <Col sm={8}>
                <Row>
                  <Col md={8} lg={6} xxl={5}>
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('INPUT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => onCreate()} textButton={t(`CREDIT_FEE.FORM.ADD_CF`)} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={creditFees.map((val, i) => {
                return renderData(val, i)
              })}
              tableFixedWidth
              tableMinWidth={1536}
            />
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}
